import React from 'react';

import type {
  ComponentRenderProps,
  DefinitionListProps,
} from '../../../types/dynamic_views';
import { useManifestId } from '../ManifestContext';
import { renderTextWithIcons } from '../utils/emojiToIcon';

/**
 * Class for an emoji-derived icon rendered INSIDE a run of text. The trailing
 * `mr-1` is inline spacing within a text flow — there is no flex parent here to
 * carry a `gap`. Hoisted so the four call sites below can't drift apart.
 */
const INLINE_ICON_CLASS = 'w-4 h-4 inline-block relative -top-[2px] mr-1';

/**
 * Definition list — a layout container for term/definition pairs. NOT
 * interactive. The previous implementation made every term and every
 * definition independently clickable (cursor-pointer + role="button" +
 * tabIndex=0 + onClick + onKeyDown) whenever the parent renderer passed an
 * `onInteraction` handler. The handler the brief renderer passes only
 * responds to 'compose_email' and 'open_proposal' — never the generic
 * 'click' this component emitted — so every click was a no-op while every
 * row visually claimed to be interactive. Removed the affordance entirely.
 *
 * If a future use case genuinely needs row-clicking, add an explicit
 * `clickable` prop to DefinitionListProps and gate the affordance on that.
 */
export const DefinitionListComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const props = component.props as DefinitionListProps;
  const { items, layout = 'default', spacing = 'normal' } = props;
  const manifestId = useManifestId();

  // Build CSS classes
  const spacingClasses = {
    tight: 'space-y-0',
    normal: 'space-y-1',
    loose: 'space-y-1.5',
  };

  const layoutClasses = {
    default: 'block',
    horizontal: 'grid grid-cols-1 md:grid-cols-3 gap-4',
  };

  // For horizontal layout, use gap-4 for spacing (already in layoutClasses)
  // For default layout, use space-y-* for vertical spacing between items
  const allClasses = [
    layoutClasses[layout] || layoutClasses.default,
    layout !== 'horizontal'
      ? spacingClasses[spacing] || spacingClasses.normal
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Validate data
  if (!items || !Array.isArray(items) || items.length === 0) {
    return (
      <div className='border border-yellow-300 bg-yellow-50 p-4 rounded'>
        <div className='text-yellow-800 font-medium'>No definition items</div>
        <div className='text-yellow-600 text-sm'>
          No items provided for the definition list
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-lg border border-border-secondary bg-bg-secondary/50 p-3'>
      <dl className={allClasses} data-component-id={component.id}>
        {(() => {
          const seen = new Map<string, number>();
          return items.map(item => {
            // Support both 'definition' (TypeScript type) and 'description' (LLM output)
            const { term, definition, description } = item as {
              term: unknown;
              definition?: unknown;
              description?: unknown;
            };
            // Unlike ListComponent, term/definition are not extracted from
            // further-nested object shapes — they're guarded-and-blanked
            // rather than recovered, since this schema doesn't expect
            // object-shaped content. Only `list` items get object-shape
            // tolerance, per the production incident that motivated it.
            if (typeof term !== 'string') {
              console.warn(
                'DefinitionListComponent: term is not a string',
                typeof term
              );
            }
            const safeTerm = typeof term === 'string' ? term : '';
            const rawValue = definition ?? description ?? '';
            if (typeof rawValue !== 'string') {
              console.warn(
                'DefinitionListComponent: definition is not a string',
                typeof rawValue
              );
            }
            const value = typeof rawValue === 'string' ? rawValue : '';
            const dupCount = seen.get(safeTerm) ?? 0;
            seen.set(safeTerm, dupCount + 1);
            const itemKey = `${safeTerm}::${dupCount}`;

            if (layout === 'horizontal') {
              return (
                <React.Fragment key={itemKey}>
                  <dt className='font-semibold text-fg-primary'>
                    {renderTextWithIcons(
                      safeTerm,
                      INLINE_ICON_CLASS,
                      manifestId
                    )}
                  </dt>
                  <dd className='text-fg-secondary md:col-span-2'>
                    {renderTextWithIcons(value, INLINE_ICON_CLASS, manifestId)}
                  </dd>
                </React.Fragment>
              );
            } else {
              // Wrap each term/definition pair in a div so space-y-* applies between pairs, not between dt/dd
              return (
                <div key={itemKey}>
                  <dt className='font-semibold text-fg-primary text-sm leading-tight'>
                    {renderTextWithIcons(
                      safeTerm,
                      INLINE_ICON_CLASS,
                      manifestId
                    )}
                  </dt>
                  <dd className='text-fg-secondary text-sm leading-tight'>
                    {renderTextWithIcons(value, INLINE_ICON_CLASS, manifestId)}
                  </dd>
                </div>
              );
            }
          });
        })()}
      </dl>
    </div>
  );
};

export default DefinitionListComponent;
